import { API_ORIGIN } from "../config/api";

/** Placeholder values sometimes stored in DB instead of real paths. */
const INVALID_MEDIA = /^(url\d+|placeholder|none|null|undefined|#|n\/a)$/i;

const HAS_IMAGE_EXT = /\.(jpe?g|png|gif|webp|svg|bmp)(\?.*)?$/i;

/**
 * Normalize upload paths to same-origin `/uploads/...` so CRA's dev proxy can
 * fetch them without cross-origin CORP issues.
 */
function toUploadPath(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  if (trimmed.startsWith("/uploads/")) return trimmed;
  if (trimmed.startsWith("uploads/")) return `/${trimmed}`;

  try {
    const url = new URL(trimmed);
    if (url.pathname.startsWith("/uploads/")) return url.pathname;
    // Absolute URL on our API host(s) — use path only for dev proxy
    const host = url.hostname.toLowerCase();
    if (
      host === "localhost" ||
      host.endsWith("viaspark.site") ||
      url.origin === API_ORIGIN
    ) {
      return url.pathname.startsWith("/uploads/") ? url.pathname : null;
    }
  } catch {
    // not a full URL
  }

  return null;
}

/**
 * Resolve a product/vendor/profile image path for use in <img src>.
 * Returns "" when there is no valid image (caller should use a local fallback asset).
 */
export function resolveMediaUrl(path: string | null | undefined): string {
  if (!path || typeof path !== "string") return "";

  const trimmed = path.trim();
  if (!trimmed || INVALID_MEDIA.test(trimmed)) return "";

  const uploadPath = toUploadPath(trimmed);
  if (uploadPath) return `${API_ORIGIN}${uploadPath}`;

  if (/^https?:\/\//i.test(trimmed)) return trimmed;

  if (HAS_IMAGE_EXT.test(trimmed)) {
    const filename = trimmed.replace(/^\//, "");
    return `${API_ORIGIN}/uploads/product-images/${filename}`;
  }

  return "";
}

export function resolveFirstMediaUrl(
  paths: string | string[] | null | undefined,
): string {
  if (!paths) return "";
  const list = Array.isArray(paths) ? paths : [paths];
  for (const p of list) {
    const resolved = resolveMediaUrl(p);
    if (resolved) return resolved;
  }
  return "";
}
